import { Form, Layout, Typography } from "@npco/zeller-design-system";
import type { useForm } from "@npco/zeller-design-system/form";
import type { ReactNode } from "react";
import type { z } from "zod";

import { formatCurrency } from "../../../../../@lib-i18n";
import { Layout as ModalLayout } from "../../../shared/layout";
import type { PagePaymentMethod } from "../page";

/**
 * The shell both link forms share: the amount, the surcharge note, and the
 * send/back actions. `children` receives `isSubmitting` and supplies the one
 * field that differs between them.
 */
export function PaymentMethodLinkForm<TSchema extends z.ZodSchema>({
	page,
	defaultLayoutProps,
	form,
	children,
}: Readonly<{
	page: PagePaymentMethod & { onClose: () => void };
	defaultLayoutProps: ModalLayout.Defaults;
	form: ReturnType<typeof useForm<TSchema>>;
	children: (isSubmitting: boolean) => ReactNode;
}>) {
	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<ModalLayout
					{...defaultLayoutProps}
					actions={[
						{ label: "Send Link", onClick: form.handleSubmit, primary: true },
						{ label: "Back", onClick: page.onBack },
					]}
					onClose={page.onClose}
					pending={isSubmitting ? "submit" : undefined}
				>
					<Layout.Container className="p-32">
						<Layout.Article>
							<div className="flex flex-col items-center gap-12 text-center">
								<Typography variant="heading-2xl">
									{formatCurrency({
										amountInCents: page.total,
										currency: page.currency,
									})}
								</Typography>
								{page.surcharge ? (
									<Typography variant="body-sm" className="text-grey-550">
										{`incl. ${formatCurrency({
											amountInCents: page.surcharge,
											currency: page.currency,
										})} surcharge`}
									</Typography>
								) : null}
							</div>
							<Layout.Section>
								<Form onSubmit={form.handleSubmit}>
									{children(isSubmitting)}
								</Form>
							</Layout.Section>
						</Layout.Article>
					</Layout.Container>
				</ModalLayout>
			)}
		</form.Subscribe>
	);
}
